import { HttpErrorResponse } from '@angular/common/http';

export type GoogleSsoStep = 'credentials' | 'twoFactor';

export interface GoogleSsoOutcome {
  nextStep: GoogleSsoStep;
  // Null when the backend is asking for a 2FA code rather than reporting a failure.
  messageKey: string | null;
  keepCredential: boolean;
}

const KEY_PREFIX = 'LOGIN_PAGE.ERROR.GOOGLE.';

const RETRY_CODE_KEYS: Record<string, string> = {
  TWO_FA_CODE_IS_WRONG: `${KEY_PREFIX}TWO_FA_CODE_IS_WRONG`,
  TWO_FA_CODE_IS_INVALID: `${KEY_PREFIX}TWO_FA_CODE_IS_WRONG`,
  TWO_FA_CODE_ERROR: `${KEY_PREFIX}TWO_FA_CODE_IS_WRONG`,
};

const RESTART_KEYS: Record<string, string> = {
  GOOGLE_USER_NOT_FOUND: `${KEY_PREFIX}USER_NOT_FOUND`,
  GOOGLE_SUBJECT_MISMATCH: `${KEY_PREFIX}SUBJECT_MISMATCH`,
  GOOGLE_DOMAIN_NOT_ALLOWED: `${KEY_PREFIX}DOMAIN_NOT_ALLOWED`,
  GOOGLE_EMAIL_NOT_VERIFIED: `${KEY_PREFIX}EMAIL_NOT_VERIFIED`,
  SSO_DISABLED_FOR_USER: `${KEY_PREFIX}SSO_DISABLED`,
  USER_IS_INACTIVE: `${KEY_PREFIX}USER_INACTIVE`,
  GOOGLE_TOKEN_EXPIRED: `${KEY_PREFIX}TOKEN_EXPIRED`,
  GOOGLE_TOKEN_INVALID: `${KEY_PREFIX}TOKEN_INVALID`,
  TOO_MANY_LOGIN_ATTEMPTS: `${KEY_PREFIX}TOO_MANY_ATTEMPTS`,
};

export function isTwoFactorRequired(error: HttpErrorResponse): boolean {
  return serverCode(error) === 'TWO_FA_CODE_IS_EMPTY';
}

export function mapGoogleSsoError(error: HttpErrorResponse): GoogleSsoOutcome {
  const code = serverCode(error);

  if (code === 'TWO_FA_CODE_IS_EMPTY') {
    return { nextStep: 'twoFactor', messageKey: null, keepCredential: true };
  }

  if (code && RETRY_CODE_KEYS[code]) {
    return {
      nextStep: 'twoFactor',
      messageKey: RETRY_CODE_KEYS[code],
      keepCredential: true,
    };
  }

  return {
    nextStep: 'credentials',
    messageKey: (code && RESTART_KEYS[code]) || 'LOGIN_PAGE.ERROR.INTERNAL_SERVER_ERROR',
    keepCredential: false,
  };
}

export function readEmailFromCredential(credential: string): string | null {
  const payload = credential?.split('.')[1];
  if (!payload) {
    return null;
  }

  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const email: unknown = JSON.parse(json).email;

    return typeof email === 'string' && email.length > 0 ? email : null;
  } catch {
    return null;
  }
}

function serverCode(error: HttpErrorResponse): string | null {
  const message: unknown = error?.error?.message;

  return typeof message === 'string' && message.length > 0 ? message : null;
}
